import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, FileText, LifeBuoy, ArrowRight, Activity } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { projectService } from '@/lib/projectService';
import { proposalService } from '@/lib/proposalService';
import { useTicketState } from '@/hooks/useTicketState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

export const PortalDashboard: React.FC = () => {
  const { user, profile } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    activeProjects: 0,
    pendingProposals: 0,
  });

  // For tickets we can just use the hook
  const { tickets, loading: ticketsLoading } = useTicketState();
  const openTicketsCount = tickets.filter(
    (t) => (t.client_id === user?.id || t.created_by === user?.id) && t.status !== 'closed' && t.status !== 'resolved'
  ).length;

  const loadMetrics = useCallback(async () => {
    if (!currentWorkspace || !user) return;
    try {
      setLoading(true);
      const [projectsData, proposalsData] = await Promise.all([
        projectService.getProjectsForClient(currentWorkspace.id, user.id),
        proposalService.listClientProposals(user.id),
      ]);

      const activeProjects = projectsData.filter((p) => p.status !== 'completed' && p.status !== 'cancelled').length;
      const pendingProposals = proposalsData.filter((p) => p.status === 'pending').length;

      setMetrics({
        activeProjects,
        pendingProposals,
      });
    } catch (error) {
      console.error('Error loading dashboard metrics:', error);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id, user?.id]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const isLoading = loading || ticketsLoading;

  const displayName = profile?.first_name || profile?.email?.split('@')[0] || 'there';

  return (
    <div className="w-full p-6 md:p-10">
      <Helmet>
        <title>Dashboard | Client Portal</title>
      </Helmet>

      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 text-foreground">
          Welcome back, {displayName}!
        </h1>
        <p className="text-muted-foreground">Here is a quick summary of your account activity.</p>
      </div>

      {isLoading ? (
        null
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Projects Card */}
          <div className="bg-card border border-border rounded-xl p-6 flex flex-col justify-between hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <FolderKanban className="w-6 h-6 text-blue-500" />
              </div>
            </div>
            <div>
              <h3 className="text-3xl font-bold text-foreground mb-1">{metrics.activeProjects}</h3>
              <p className="text-sm text-muted-foreground font-medium">Active Projects</p>
            </div>
            <button
              onClick={() => navigate('/projects')}
              className="mt-6 flex items-center gap-2 text-sm font-semibold text-workspace-accent hover:text-workspace-accent-hover transition-colors"
            >
              View Projects <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Proposals Card */}
          <div className="bg-card border border-border rounded-xl p-6 flex flex-col justify-between hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-amber-500" />
              </div>
            </div>
            <div>
              <h3 className="text-3xl font-bold text-foreground mb-1">{metrics.pendingProposals}</h3>
              <p className="text-sm text-muted-foreground font-medium">Pending Proposals</p>
            </div>
            <button
              onClick={() => navigate('/proposals')}
              className="mt-6 flex items-center gap-2 text-sm font-semibold text-workspace-accent hover:text-workspace-accent-hover transition-colors"
            >
              Review Proposals <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Tickets Card */}
          <div className="bg-card border border-border rounded-xl p-6 flex flex-col justify-between hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-rose-500/10 flex items-center justify-center">
                <LifeBuoy className="w-6 h-6 text-rose-500" />
              </div>
            </div>
            <div>
              <h3 className="text-3xl font-bold text-foreground mb-1">{openTicketsCount}</h3>
              <p className="text-sm text-muted-foreground font-medium">Open Support Tickets</p>
            </div>
            <button
              onClick={() => navigate('/support')}
              className="mt-6 flex items-center gap-2 text-sm font-semibold text-workspace-accent hover:text-workspace-accent-hover transition-colors"
            >
              Get Support <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Getting Started / Next Steps Placeholder */}
      <div className="bg-card border border-border rounded-xl p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-workspace-accent/10 flex items-center justify-center">
            <Activity className="w-5 h-5 text-workspace-accent" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Getting Started</h2>
        </div>
        <p className="text-muted-foreground mb-6">
          Navigate through the portal using the sidebar menu. You can track project progress, approve proposals, access your files, and submit support tickets directly from here.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/projects')}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-border hover:bg-muted text-sm font-medium transition-colors"
          >
            Check Project Status
          </button>
          <button
            onClick={() => navigate('/support')}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-border hover:bg-muted text-sm font-medium transition-colors"
          >
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};
